import { cn } from "@app/ui";

import type { I18nKey } from "../../lib/language.js";
import { useI18n } from "../../lib/useI18n.js";
import { formatDurationCompact } from "../../lib/format.js";
import { useMatrixViewStore, useSettingsStore } from "../../stores/index.js";
import { HORIZONS, type Horizon } from "./model.js";

const HORIZON_LABEL: Record<Horizon, I18nKey> = {
  today: "priorities.horizon.today",
  d3: "priorities.horizon.d3",
  d7: "priorities.horizon.d7",
  d14: "priorities.horizon.d14",
  d30: "priorities.horizon.d30",
  all: "priorities.horizon.all",
};

interface Props {
  openCount: number;
  totalMinutes: number;
}

/** Horizon picker + include-toggles + range summary for the Priorities view.
 *  All state lives in `useMatrixViewStore` (device-local prefs). */
export function ViewControls({ openCount, totalMinutes }: Props) {
  const { t } = useI18n();
  const horizon = useMatrixViewStore((s) => s.horizon);
  const setHorizon = useMatrixViewStore((s) => s.setHorizon);
  const includeOverdue = useMatrixViewStore((s) => s.includeOverdue);
  const includeUndated = useMatrixViewStore((s) => s.includeUndated);
  const includeDone = useMatrixViewStore((s) => s.includeDone);
  const includeEvents = useMatrixViewStore((s) => s.includeEvents);
  const toggle = useMatrixViewStore((s) => s.toggle);
  // The events toggle only exists while the matrix system covers events.
  const eventsCovered = useSettingsStore((s) => s.matrixScope === "tasks_events");

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="flex flex-wrap items-center gap-1" role="radiogroup" aria-label={t("priorities.horizon.label")}>
        {HORIZONS.map((h) => (
          <Pill key={h} active={horizon === h} role="radio" ariaChecked={horizon === h} onClick={() => setHorizon(h)}>
            {t(HORIZON_LABEL[h])}
          </Pill>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <Pill active={includeOverdue} pressed onClick={() => toggle("includeOverdue")}>{t("priorities.include.overdue")}</Pill>
        <Pill active={includeUndated} pressed onClick={() => toggle("includeUndated")}>{t("priorities.include.undated")}</Pill>
        <Pill active={includeDone} pressed onClick={() => toggle("includeDone")}>{t("priorities.include.done")}</Pill>
        {eventsCovered && (
          <Pill active={includeEvents} pressed onClick={() => toggle("includeEvents")}>{t("priorities.include.events")}</Pill>
        )}
      </div>
      <span className="ml-auto text-xs text-muted-foreground">
        {totalMinutes > 0
          ? t("priorities.summary", { count: openCount, time: formatDurationCompact(totalMinutes) })
          : t("priorities.summaryNoTime", { count: openCount })}
      </span>
    </div>
  );
}

interface PillProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  /** Toggle pills expose aria-pressed; radio pills use role/aria-checked. */
  pressed?: boolean;
  role?: string;
  ariaChecked?: boolean;
}

function Pill({ active, onClick, children, pressed, role, ariaChecked }: PillProps) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={ariaChecked}
      aria-pressed={pressed ? active : undefined}
      onClick={onClick}
      className={cn(
        "inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-medium transition active:scale-95 motion-reduce:transform-none",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
